import React, { useMemo } from 'react';
import HomeIcon from '@mui/icons-material/Home';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import GroupIcon from '@mui/icons-material/Group';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import LanguageIcon from '@mui/icons-material/Language';
import { cliengoQueries } from '@cliengo/mfe-utils/hooks';

import environment from '@/config/environment';

// A curated list of navigation items for power users.
// Content (names and descriptions) are in Spanish.
export const curatedNavigationItems: INavigationItem[] = [
  {
    name: 'Inicio',
    description: 'Ir a la página principal',
    icon: <HomeIcon />,
    path: '/',
  },
  {
    name: 'Panel de control',
    description: 'Ver el panel de control',
    icon: <DashboardIcon />,
    path: '/home',
  },
  {
    name: 'Inbox',
    description: 'Acceder a Inbox',
    icon: <LiveTvIcon />,
    path: '/live',
  },
  {
    name: 'Reportes',
    description: 'Ver reportes e informes',
    icon: <AssessmentIcon />,
    path: '/reports',
  },
  {
    name: 'Soporte',
    description: 'Solicitar soporte técnico',
    icon: <SupportAgentIcon />,
    path: '/support',
  },
  {
    name: 'Equipo',
    description: 'Gestionar el equipo',
    icon: <GroupIcon />,
    path: '/team',
  },
  {
    name: 'Cuenta',
    description: 'Acceder a la cuenta',
    icon: <AccountCircleIcon />,
    path: '/account',
  },
  {
    name: 'Integraciones',
    description: 'Conectar con sistemas externos',
    icon: <IntegrationInstructionsIcon />,
    path: '/integrations',
  },
];

export type INavigationItem = {
  name: string;
  description: string;
  icon: React.ReactElement;
  path: string;
  blank?: boolean;
};

export const usePowerNavigationFeed = () => {
  const websites = cliengoQueries.websites();
  const account = cliengoQueries.account();

  const websiteUrls = useMemo(() => {
    const urls: INavigationItem[] = [];

    for (const website of websites.data || []) {
      urls.push({
        name: `Entrenamiento de ${website.title}`,
        description: `Entrenar a ${website.title}`,
        icon: <LanguageIcon />,
        path: `/chatbot/${website.id}/training-data`,
      });
      urls.push({
        name: `Conversación de ${website.title}`,
        description: `Ver conversación de ${website.title}`,
        icon: <LanguageIcon />,
        path: `/chatbot/${website.id}/conversation`,
      });
      urls.push({
        name: `Personalidad de ${website.title}`,
        description: `Configurar personalidad de ${website.title}`,
        icon: <LanguageIcon />,
        path: `/chatbot/${website.id}/personality`,
      });
      urls.push({
        name: `Apariencia de ${website.title}`,
        description: `Configurar apariencia de ${website.title}`,
        icon: <LanguageIcon />,
        path: `/chatbot/${website.id}/appearance`,
      });
      urls.push({
        name: `Conexiones de ${website.title}`,
        description: `Configurar conexiones de ${website.title}`,
        icon: <LanguageIcon />,
        path: `/chatbot/${website.id}/install`,
      });
      urls.push({
        name: `Integraciones de ${website.title}`,
        description: `Configurar integraciones de ${website.title}`,
        icon: <LanguageIcon />,
        path: `/chatbot/${website.id}/integrations`,
      });
      urls.push({
        name: `Probar chatbot de ${website.title}`,
        description: `Probar chatbot de ${website.title}`,
        icon: <LanguageIcon />,
        path: `${environment.LW_URL}/?companyId=${account.data?.id}&websiteId=${website.id}&restartConversation=true`,
        blank: true,
      });
      urls.push({
        name: website.title,
        description: `Configurar ${website.title}`,
        icon: <LanguageIcon />,
        path: `/chatbot/${website.id}`,
      });
    }
    return urls;
  }, [websites.data, account.data]);

  if (websites.isLoading || account.isLoading) {
    return [] as INavigationItem[];
  }

  const result = [...curatedNavigationItems, ...(websiteUrls || [])];

  return result;
};
